import { cloneDeep } from 'es-toolkit'
import { Effect } from '../event/effect';
import { convertElementCoord2DeviceToCoord, convertCompassCoord2DeviceToCoord, calcCompassFivePoint, ratioXY, calcAreaCenterXY } from '../translate/control';

import { type TEditControlInteractive, type TInputEvent, type IControlStateInfo, type TLocusStateInfo, type IPresetKeyboardInteractiveBlock, EControlEventName, EControlTouchType, EControlSystemKey, ELocusStatus, EPresetKeyboardAction, ECompassDirection } from '../types/control'
import { type IWsEffectCallback, type IWsSend, EWSHookName } from '../types/socket';
import { type IRTCDataChannelSend, ERTCEventName } from '../types/rtc';
import { generateRandomId } from '../utils';

export class Control extends Effect {
  isPress: boolean = false;
  isRegisteredKeyboardListener: boolean = false;

  wsSend: IWsSend;
  wsEffect: IWsEffectCallback;
  dataChannelSend: IRTCDataChannelSend;

  status: IControlStateInfo = {
    isHorizontal: false,
    isInputStatus: false,
    isFreezeControl: false,
    configKeyboard: { logic: {}, interactive: {} }
  }

  // video rect
  rect = {
    left: 0,
    top: 0,
    width: 0,
    height: 0
  }

  // input status
  prevInputText = '';
  inputStatusKeyboardElement: HTMLInputElement | null = null;
  fullscreenElementId = '#fullscreen-element';

  // active keyboard
  activeKeyMap = new Set();
  activeCompassMap = new Map();

  // listener
  keydownListener: ((event: KeyboardEvent) => void) | null = null;
  keyupListener: ((event: KeyboardEvent) => void) | null = null;
  wheelListener: ((event: WheelEvent) => void) | null = null;

  // compass
  compassMoveInterval = 3;
  compassInterval: null | NodeJS.Timeout = null;

  // locus
  locusList: Map<string, TLocusStateInfo> = new Map();

  // wheel
  wheelStatus: { isWheeling: boolean, startXY: Record<'x' | 'y', number>, prevXY: Record<'x' | 'y', number>, zoom1XY: Record<'x' | 'y', number>, zoom2XY: Record<'x' | 'y', number>,  timer: null | NodeJS.Timeout } = {
    isWheeling: false,
    startXY: { x: 0, y: 0 },
    prevXY: { x: 0, y: 0 },
    zoom1XY: { x: 0, y: 0 },
    zoom2XY: { x: 0, y: 0 },
    timer: null
  }

  // screen move
  isScreenFollowMouseStatus = {
    working: false,
    key: ''
  }

  // zoom
  isInZoomStatus = false;

  constructor(wsSend: IWsSend, wsEffect: IWsEffectCallback, dataChannelSend: IRTCDataChannelSend) {
    super();
    this.wsSend = wsSend;
    this.wsEffect = wsEffect;
    this.dataChannelSend = dataChannelSend;

    this.listenerToggleInputStatus();
  }

  getStatusInfo () {
    return this.status;
  }

  listenerToggleInputStatus() {
    this.wsEffect<{ data: string }>((response) => {
      try {
        const _data: { IMEIsActive: boolean } = JSON.parse(response.data);
        this.status.isInputStatus = _data.IMEIsActive;

        if (this.status.isInputStatus) {
          !this.inputStatusKeyboardElement && this.initInputElement();
          this.inputStatusKeyboardElement?.focus();
        } else {
          this.inputStatusKeyboardElement?.remove();
          this.inputStatusKeyboardElement = null;
        }
      } catch (err) { }
    }, [EWSHookName.INPUT_STATUS])
  }

  consumeLocusData (key: string = '', value: TLocusStateInfo) {
    if (this.status.isFreezeControl) return;
    const { status, x, y } = value;

    switch (status) {
      case ELocusStatus.DOWN:
        if (!this.locusList.size) {
          const isFirstTouch = key.startsWith('touch-') && !this.activeKeyMap.size;
          const isFirstKeydown = this.activeKeyMap.size === 1;
          if (isFirstTouch || isFirstKeydown) {
            this.dataChannelSend(ERTCEventName.TOUCH, { type: EControlTouchType.DOWN, x, y });
            break;
          }
        }
        // move
        this.consumeLocusData(key, { x, y, status: ELocusStatus.MOVE });
        break;
      case ELocusStatus.MOVE:
          let fingerLocusArr: Array<Record<'x' | 'y' | 'id', number>> = [];
          if (key) {
            this.locusList.set(key, { x, y, status: ELocusStatus.MOVE });
          }
          this.locusList.forEach((locus) => {
            if (locus.status === ELocusStatus.MOVE) {
              fingerLocusArr.push({ x: locus.x, y: locus.y, id: fingerLocusArr.length });
            }
          });
          this.dataChannelSend(ERTCEventName.MOVE, fingerLocusArr);
        break;
      case ELocusStatus.UP:
        this.locusList.delete(key);
        this.activeKeyMap.delete(key);

        if (!this.activeKeyMap.size && !this.locusList.size) {
          this.dataChannelSend(ERTCEventName.TOUCH, { type: EControlTouchType.UP, x, y });
        } else {
          // fake-up => move convert up => move array del current key
          this.consumeLocusData('', { status: ELocusStatus.MOVE, x: 0, y: 0 });
        }
        break;
    }
  }

  initInputElement() {
    this.inputStatusKeyboardElement = document.createElement("input");
    this.inputStatusKeyboardElement.setAttribute('type', 'text');
    this.inputStatusKeyboardElement.setAttribute('style', 'position: fixed; top: -1000px; left: 0; opacity: 0; z-index: -10; pointer-event: none;');
    this.inputStatusKeyboardElement.addEventListener('input', (e) => this.handleTextInput(e as TInputEvent));
    this.inputStatusKeyboardElement.addEventListener('compositionend', (e) => this.handleTextInput(e as unknown as TInputEvent, true));
    this.inputStatusKeyboardElement.addEventListener('paste', (e) => {
      let paste = (e.clipboardData || window?.clipboardData).getData("text");
      this.handleTextInput({ data: paste } as TInputEvent, true);
    });
    document.querySelector(this.fullscreenElementId)?.appendChild(this.inputStatusKeyboardElement);
  }

  // text input
  handleTextInput(evt: TInputEvent, endComposition = false) {
    if (evt.isComposing && !endComposition) return;
    this.dataChannelSend(ERTCEventName.INPUT, { msg: evt.data })
  }

  updateStatusInfo(newInfo: IControlStateInfo) {
    if (newInfo.configKeyboard && newInfo.configKeyboard !== this.status.configKeyboard) {
      this.hookEvent(EControlEventName.KEYBOARD_DATA_UPDATE, { data: newInfo.configKeyboard });
    }
    this.status = cloneDeep({ ...this.status, ...newInfo })
  }

  handleCompassTask () {
    if (this.compassInterval) return;
    this.compassInterval = setInterval(() => {
      this.activeCompassMap.forEach((compass, key) => {
        const nextXY = convertCompassCoord2DeviceToCoord(compass, this.status.isHorizontal);
        this.activeCompassMap.set(key, {
          ...compass,
          prevHotPressSize: compass.hotPress.size,
          deviceCoord: nextXY
        });
        this.consumeLocusData(key, { x: nextXY.x, y: nextXY.y, status: ELocusStatus.MOVE });
      });

      // auto stop
      if (!this.activeCompassMap.size && this.compassInterval) {
        clearInterval(this.compassInterval);
        this.compassInterval = null;
      }
    }, this.compassMoveInterval);
  }

  setControlLayerElement(controlLayerElement: HTMLElement | undefined) {
    if (controlLayerElement) {
      const observer = this.setRect();
      observer.observe(controlLayerElement);

      // keyboard events
      if (!this.isRegisteredKeyboardListener) {
        this.keydownListener = this.keydown.bind(this);
        this.keyupListener = this.keyup.bind(this);
        this.wheelListener = this.wheel.bind(this);
        window.addEventListener("keydown", this.keydownListener, true);
        window.addEventListener("keyup", this.keyupListener, true);
        controlLayerElement.addEventListener("wheel", this.wheelListener, { passive: false });
        // controlLayerElement.addEventListener("mouseout", () => {
        //   if (this.locusList.size) {
        //     this.isPress = false;
        //     this.activeCompassMap.clear();
        //     this.activeKeyMap.clear();
        //     this.locusList.clear();
        //     this.dataChannelSend(ERTCEventName.TOUCH, { type: EControlTouchType.UP, x: 0, y: 0 });
        //   }
        // })
        this.isRegisteredKeyboardListener = true;
      }
    }
  }

  checkComposeIsActive(composition: Array<string> | undefined) {
    if (!composition) return false;
    let matchedCompositionCount = 0;
    composition.forEach((otherCompositionCode) => {
      if (this.activeKeyMap.has(otherCompositionCode)) {
        matchedCompositionCount += 1;
      }
    });
    return matchedCompositionCount === composition.length;
  }

  triggerSystemKey (x: EControlSystemKey) {
    this.dataChannelSend(ERTCEventName.SYSTEM_KEY, { type: '0', x });
  }

  wheel (e: WheelEvent) {
    if (this.activeKeyMap.size) {
      if (!this.isInZoomStatus) return;
    }
    e.preventDefault();

    this.wheelStatus.timer && clearTimeout(this.wheelStatus.timer);
    this.wheelStatus.timer = setTimeout(() => {
      this.wheelStatus.isWheeling = false;
      this.consumeLocusData('system-wheel-scroll', { status: ELocusStatus.UP, ...this.wheelStatus.prevXY });
    }, 100);

    if (!this.wheelStatus.isWheeling) {
      this.wheelStatus.isWheeling = true;
      let [{ x, y }] = convertElementCoord2DeviceToCoord(e, this.rect, this.status.isHorizontal);
      if (this.status.isHorizontal) {
        y += e.deltaY;
      }
      !this.isInZoomStatus && this.consumeLocusData('system-wheel-scroll', { status: ELocusStatus.DOWN, x, y });
      this.wheelStatus.startXY = { x, y };
      this.wheelStatus.prevXY = { x, y };
      this.wheelStatus.zoom1XY = { x, y: e.deltaY > 0 ? y + 400 : y };
      this.wheelStatus.zoom2XY = { x, y: e.deltaY > 0 ? y - 400 : y };
      return;
    }

    // mock scroll
    if (!this.isInZoomStatus) {
      let count = 0;
      while (count < 10) {
        const salt = e.deltaY > 0 ? 2 * count : -2 * count;
        this.status.isHorizontal ? (this.wheelStatus.prevXY.x += salt) : (this.wheelStatus.prevXY.y += salt);
        this.consumeLocusData('system-wheel-scroll', { status: ELocusStatus.MOVE, ...this.wheelStatus.prevXY });
        count += 1;
      }
    } else {
      // mock zoom
      let count = 0;
      while (count < 10) {
        const isZoomIN = e.deltaY < 0;
        if (isZoomIN) {
          this.wheelStatus.zoom1XY.y += 2
          this.wheelStatus.zoom2XY.y -= 2
        } else {
          this.wheelStatus.zoom1XY.y -= 2
          this.wheelStatus.zoom2XY.y += 2
        }
        this.consumeLocusData('zoom-by-wheel-1', { status: ELocusStatus.MOVE, ...this.wheelStatus.zoom1XY })
        this.consumeLocusData('zoom-by-wheel-2', { status: ELocusStatus.MOVE, ...this.wheelStatus.zoom2XY })
        count += 1;
      }
    }
  }

  keydown(event: KeyboardEvent) {
    /*
      Q: why key code 229?
      A: https://bugzilla.mozilla.org/show_bug.cgi?id=354358
    */
    if (event.isComposing || event.keyCode === 229) {
      return;
    }

    this.hookEvent(EControlEventName.KEYBOARD_DOWN, { event });

    if (this.status.isInputStatus) {
      const idx = ['Delete', 'Backspace', 'Enter'].findIndex(v => v === event.key);
      if (idx < 0) return;
      this.triggerSystemKey(idx > 1 ? EControlSystemKey.ENTER : EControlSystemKey.DELETE);
    } else {
      // in game status
      const matchedKeyData = this.status.configKeyboard?.logic[event.code];
      if (matchedKeyData && matchedKeyData.length) {
        event.preventDefault();
        if (this.activeKeyMap.has(event.code)) return;
        this.activeKeyMap.add(event.code);
        matchedKeyData.map(keyBlock => {
          const interactive = this.status.configKeyboard?.interactive[keyBlock.interactive];
          if (keyBlock.isCompose && !this.checkComposeIsActive(keyBlock.composition)) return;
          if (interactive) {
            const { width, x, y } = interactive;
            switch (keyBlock.patch) {
              case EPresetKeyboardAction.TOUCH:
                const { centerX: btnCenterX, centerY: btnCenterY } = calcAreaCenterXY(width, x, y, this.rect.width, this.rect.height, this.status.isHorizontal);
                this.consumeLocusData(keyBlock.interactive, { status: ELocusStatus.DOWN, x: btnCenterX, y: btnCenterY });
                break;
              case EPresetKeyboardAction.COMPASS:
                let compassInfo = this.activeCompassMap.get(keyBlock.interactive);
                if (!compassInfo) {
                  // first time event current compass need to send down event
                  const fivePoints = calcCompassFivePoint(width, x, y, this.rect.width, this.rect.height, this.status.isHorizontal);
                  compassInfo = {
                    fivePoints,
                    hotPress: new Set(),
                    prevHotPressSize: 0
                  };
                  this.consumeLocusData(keyBlock.interactive, { status: ELocusStatus.DOWN, x: fivePoints.centerX, y: fivePoints.centerY });
                }
                compassInfo.hotPress.add(keyBlock.direction);
                this.activeCompassMap.set(keyBlock.interactive, compassInfo);
                this.handleCompassTask();
                break;
              case EPresetKeyboardAction.ZOOM:
                this.isInZoomStatus = true;
                this.consumeLocusData('zoom-by-wheel-1', { status: ELocusStatus.DOWN, x, y });
                break;
            }
          }
          this.hookEvent(EControlEventName.PRESET_KEYBOARD_DOWN, { code: event.code, uiId: matchedKeyData.map(v => v.interactive), activeKeyMap: this.activeKeyMap });
        });
      }
    }
  }

  keyup(event: KeyboardEvent) {
    this.hookEvent(EControlEventName.KEYBOARD_UP, { event });
    if (this.status.isInputStatus) return;
    const matchedKeyData = this.status.configKeyboard?.logic[event.code];
    if (matchedKeyData && matchedKeyData.length) {
      event.preventDefault();
      this.activeKeyMap.delete(event.code);
      matchedKeyData.map(keyBlock => {
        const interactive = this.status.configKeyboard?.interactive[keyBlock.interactive];
        if (interactive) {
          const { x, y, width } = interactive;
          if (keyBlock.patch === EPresetKeyboardAction.TOUCH) {
            const { centerX: btnCenterX, centerY: btnCenterY } = calcAreaCenterXY(width, x, y, this.rect.width, this.rect.height, this.status.isHorizontal);
            this.consumeLocusData(keyBlock.interactive, { status: ELocusStatus.UP, x: btnCenterX, y: btnCenterY });
          } else if (keyBlock.patch === EPresetKeyboardAction.COMPASS) {
            const compassInfo = this.activeCompassMap.get(keyBlock.interactive);
            if (compassInfo) {
              compassInfo.hotPress.delete(keyBlock.direction);
              if (!compassInfo.hotPress.size) {
                this.activeCompassMap.delete(keyBlock.interactive)
                // last time event current compass need to send up event
                this.consumeLocusData(keyBlock.interactive, { status: ELocusStatus.UP, x: compassInfo.fivePoints.centerX, y: compassInfo.fivePoints.centerY });
              } else {
                this.activeCompassMap.set(keyBlock.interactive, { ...compassInfo });
              }
            }
          } else if (keyBlock.patch === EPresetKeyboardAction.ROTATION) {
            this.isScreenFollowMouseStatus.working = !this.isScreenFollowMouseStatus.working;
            this.isScreenFollowMouseStatus.key = keyBlock.interactive;
            this.consumeLocusData(keyBlock.interactive, { status: this.isScreenFollowMouseStatus.working ? ELocusStatus.DOWN : ELocusStatus.UP, x, y });
            this.hookEvent(EControlEventName.TOGGLE_SCREEN_FOLLOW_SCREEN_STATUS, { status: this.isScreenFollowMouseStatus.working });
          } else if (keyBlock.patch === EPresetKeyboardAction.ZOOM) {
            this.isInZoomStatus = false;
            this.consumeLocusData('zoom-by-wheel-1', { status: ELocusStatus.UP, ...this.wheelStatus.zoom1XY });
            this.consumeLocusData('zoom-by-wheel-2', { status: ELocusStatus.UP, ...this.wheelStatus.zoom2XY });
          }
        }
      });
      this.hookEvent(EControlEventName.PRESET_KEYBOARD_UP, { code: event.code, uiId: matchedKeyData.map(v => v.interactive), activeKeyMap: this.activeKeyMap });
    }
  }

  setRect() {
    try {
      return new ResizeObserver((entries) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }
          for (const entry of entries) {
            if (entry.target) {
              const { left, top, width, height } = entry.target.getBoundingClientRect();
              this.rect = { left, top, width, height };
              // notify outside
              this.hookEvent(EControlEventName.CONTROL_RECT_RESIZE, this.rect);
            }
          }
        })
      });
    } catch (err) {}
  }

  down(e: MouseEvent | TouchEvent) {
    this.isPress = true;
    const [{ x, y, id }] = convertElementCoord2DeviceToCoord(e, this.rect, this.status.isHorizontal);
    const data = { type: EControlTouchType.DOWN, x, y };
    this.consumeLocusData(`touch-${id || 0}`, { status: ELocusStatus.DOWN, x, y });
    this.hookEvent(EControlEventName.MULTI_TOUCH, data);
  }

  move(e: MouseEvent | TouchEvent) {
    const { working, key } = this.isScreenFollowMouseStatus;
    if (this.isPress || working) {
      const data = convertElementCoord2DeviceToCoord(e, this.rect, this.status.isHorizontal);
      if (working && !this.activeKeyMap.size) {
        this.consumeLocusData(key, { status: ELocusStatus.DOWN, x: data[0].x, y: data[0].y });
      }
      data.map(touch => {
        const locusKey = working ? key : `touch-${touch.id || 0}`;
        this.consumeLocusData(locusKey, { status: ELocusStatus.MOVE, x: touch.x, y: touch.y });
      });
      this.hookEvent(EControlEventName.MOVE, data);
    }
  }

  up(e: MouseEvent | TouchEvent) {
    this.isPress = false;
    const [{ x, y, id }] = convertElementCoord2DeviceToCoord(e, this.rect, this.status.isHorizontal);
    const data = { type: EControlTouchType.UP, x, y };
    this.consumeLocusData(`touch-${id || 0}`, { status: ELocusStatus.UP, x, y });
    this.hookEvent(EControlEventName.MULTI_TOUCH, data);
  }

  interactiveUnbindLogic (interactiveCode: string[], id: string, direction?: ECompassDirection) {
    interactiveCode.forEach(keyCode => {
      if (this.status.configKeyboard && this.status.configKeyboard.logic[keyCode]) {
        this.status.configKeyboard.logic[keyCode] = this.status.configKeyboard.logic[keyCode]?.filter(val => {
          if (direction === undefined) {
            // other type control area
            return val.interactive !== id;
          } else if (val.interactive === id) {
            // compass
            return Number(direction) !== Number(val.direction);
          }
        });

        if (!this.status.configKeyboard.logic[keyCode].length) {
          delete this.status.configKeyboard.logic[keyCode]
        }
      }
    });
  }

  interactiveBindLogic (interactiveCode: string[], type: EPresetKeyboardAction, id: string, direction?: ECompassDirection) {
    interactiveCode.forEach((_code, idx) => {
      if (this.status.configKeyboard) {
        const keyLogicData = this.status.configKeyboard.logic[_code] || [];
        const isCompose = interactiveCode.length > 0 && type !== EPresetKeyboardAction.COMPASS;
        let composition = undefined;
        if (isCompose) {
          composition = interactiveCode.filter(v => v !== _code);
        }
        const newKeyLogic = { patch: type, interactive: id, isCompose, direction: direction ? Number(direction) : undefined, composition };
        if (type === EPresetKeyboardAction.COMPASS) {
          newKeyLogic.direction = idx;
        }
        this.status.configKeyboard.logic[_code] = [...keyLogicData, newKeyLogic];
      }
    });
  }

  translateControlAreaRectData (interactive: TEditControlInteractive) {
    // translate x y
    if (interactive?.x && interactive?.y) {
      const { x, y } = ratioXY(interactive.x, interactive.y, this.rect.width, this.rect.height);
      interactive = { ...interactive, x, y };
    }
    // translate with height
    if (interactive?.width && interactive?.height) {
      interactive = { ...interactive, width: Math.round((interactive.width / this.rect.width) * 1000), height: Math.round((interactive.height / this.rect.width) * 1000) };
    }

    return interactive;
  }

  addControlArea (interactive: IPresetKeyboardInteractiveBlock) {
    if (!this.status.configKeyboard) return;
    // generate id
    const id = generateRandomId(this.status.configKeyboard.interactive);
    // translate rect
    interactive = { ...interactive, ...this.translateControlAreaRectData(interactive) };
    // new interactive
    this.status.configKeyboard.interactive[id] = interactive;
    // bind logic
    this.interactiveBindLogic(interactive.code, interactive.type, id);
    // notify
    this.hookEvent(EControlEventName.KEYBOARD_DATA_UPDATE, { data: this.status.configKeyboard });
  }

  setControlArea (interactive: TEditControlInteractive, id: string) {
    if (!this.status.configKeyboard) return;
    const oldData = { ...this.status.configKeyboard.interactive[id] };
    if (!oldData) return;
    // translate rect
    interactive = { ...interactive, ...this.translateControlAreaRectData(interactive) };
    // compass modify key name
    if (interactive.direction && interactive.name && oldData.type === EPresetKeyboardAction.COMPASS && typeof oldData.name === 'object') {
      interactive.name = { ...oldData.name, [interactive.direction]: interactive.name };
    }
    // modify bind key(keyboard key)
    if (interactive?.code) {
      // remove this keycode logic include current interactive id
      this.interactiveUnbindLogic(oldData.code, id, interactive.direction);
      // bind new keycode logic by current interactive
      this.interactiveBindLogic(interactive.code, oldData.type, id, interactive.direction);
    }
    // update interactive
    this.status.configKeyboard.interactive[id] = { ...oldData, ...interactive };
    // notify
    this.hookEvent(EControlEventName.KEYBOARD_DATA_UPDATE, { data: this.status.configKeyboard });
  }

  deleteControlArea (id: string) {
    if (!this.status.configKeyboard) return;
    const interactive = this.status.configKeyboard.interactive[id];
    // del logic
    this.interactiveUnbindLogic(interactive.code, id);
    // del interactive(GUI)
    delete this.status.configKeyboard.interactive[id];
    // notify
    this.hookEvent(EControlEventName.KEYBOARD_DATA_UPDATE, { data: this.status.configKeyboard });
  }

  destroy() {
    this.compassInterval && clearInterval(this.compassInterval);
    this.keydownListener && window.removeEventListener('keydown', this.keydownListener, true);
    this.keyupListener && window.removeEventListener('keyup', this.keyupListener, true);
    this.keydownListener = null;
    this.keyupListener = null;
    this.isRegisteredKeyboardListener = false;
  }
}

export function deviceControl(wsSend: IWsSend, wsEffect: IWsEffectCallback, dataChannelSend: IRTCDataChannelSend) {
  const instance = new Control(wsSend, wsEffect, dataChannelSend);
  return {
    control: instance,

    // control events
    down: instance.down.bind(instance),
    move: instance.move.bind(instance),
    up: instance.up.bind(instance),
    setControlLayerElement: instance.setControlLayerElement.bind(instance),
    triggerSystemKey: instance.triggerSystemKey.bind(instance),

    // life cycle
    destroy: instance.destroy.bind(instance),
    effect: instance.effect.bind(instance),
    getStatusInfo: instance.getStatusInfo.bind(instance),
    updateStatusInfo: instance.updateStatusInfo.bind(instance),

    // handle keyboard map data
    addControlArea: instance.addControlArea.bind(instance),
    setControlArea: instance.setControlArea.bind(instance),
    deleteControlArea: instance.deleteControlArea.bind(instance),
  }
}
